/**
 * deal-detail/render-items.ts — 품목 렌더 (구성 탭 / 읽기·편집 모드 / 카탈로그 검색 패널)
 *
 * DealDetailView.ts 에서 분리 (정리 Step 3b, 2026-07-03 — 분해 계획 v2 §2).
 * 이동만 — 동작·마크업 변화 없음. 전부 "state → HTML 문자열" 순수 함수.
 * 이벤트는 data-action 배선이라 핸들러는 뷰 메서드 그대로.
 */

import * as api from '../../api/client';
import {
  collectServerGroups, renderServerGroup, type ServerGroup,
} from './render-server-group';
import {
  sourceSelectHtml, selectedSourceValue, sourceOptions, type SourcePool,
} from '../../lib/source-options';
import { escapeHtml, catalogHitDescription } from '../../lib/cells';
import { CONDITION_OPTIONS } from '../../lib/condition';
import {
  fmtThousands, editItemFieldDirty, editConfigDirty,
  type EditConfigState, type EditDraftItem, type ItemSearchState,
} from './types';

/**
 * 활성 구성의 품목 (미배정 null 은 최저 config_no 구성에 귀속 — backfill 안전망).
 * 서버 그룹 추출·플랫 파트 렌더가 공유한다.
 */
function activeItems(
  full: api.DealFull,
  active: api.DealConfiguration | null,
): NonNullable<api.DealFull['items']> {
  const all = full.items ?? [];
  if (!active) return all;
  const lowestId = [...(full.configurations ?? [])]
    .sort((a, b) => a.config_no - b.config_no)[0]?.id;
  return all.filter((it) =>
    it.configuration_id === active.id
    || (active.id === lowestId && it.configuration_id == null));
}

/**
 * 개별 품목의 소스 select — 서버 구성품과 **같은 옵션·같은 라벨**을 쓴다.
 *
 * 레퍼런스('보기' 팝오버)가 있던 자리다. 과거 RFQ 가격을 보여주기만 하던 칸을,
 * 실제로 원가 근거를 고르는 칸으로 바꿨다(스펙아웃).
 *
 * `itemId` 가 없으면(방금 추가해 아직 저장 안 된 드래프트 행) 비활성이다 —
 * PATCH 로 즉시 저장하는 구조라 대상 행이 DB 에 있어야 한다. 소스는 RFQ 응답을
 * 보고 고르는 값이라 저장 전에 고를 일이 사실상 없다.
 */
function renderItemSourceSelect(
  pool:       SourcePool,
  optionPart: string | null,
  sourceType: string | null | undefined,
  sourceRef:  string | null | undefined,
  itemId:     string | null,
  locked:     boolean,
): string {
  if (locked) {
    // 확정 구성 — 저장된 라벨만 읽기 전용으로.
    return sourceType
      ? `<span class="deal-item-src-label">${escapeHtml(sourceLabelOf(pool, optionPart, sourceType, sourceRef))}</span>`
      : '<span class="text-muted">소스 미지정</span>';
  }
  if (!itemId) {
    return '<select class="deal-item-srcsel" disabled><option>저장 후 선택</option></select>';
  }
  return sourceSelectHtml({
    pool,
    part:     optionPart ?? '',
    selected: selectedSourceValue(sourceType, sourceRef),
    attrs:    `data-action="item-source" data-deal-item-id="${escapeHtml(itemId)}"`,
    cls:      'deal-item-srcsel',
  });
}

/** 확정 후 표시용 — 후보 목록에서 라벨을 찾고, 없으면 저장 당시 값이 없다는 뜻. */
function sourceLabelOf(
  pool: SourcePool, part: string | null,
  sourceType: string | null | undefined, sourceRef: string | null | undefined,
): string {
  const v = selectedSourceValue(sourceType, sourceRef);
  return sourceOptions(pool, part ?? '').find((o) => o.value === v)?.label ?? '소스 지정됨';
}

// 등록 상태 뱃지 — 등록:없음 / catalog_part_pending(pending)에만:임시등록 / 아무데도 없음:미등록.
function regBadge(isUnreg: boolean, isPending?: boolean): string {
  if (!isUnreg) return '';
  return isPending
    ? `<span class="cond-chip cond-임시등록">임시등록</span> `
    : `<span class="cond-chip cond-미등록">미등록</span> `;
}

// ─── 품목 섹션 (구성 탭 + 읽기/편집 모드 분기) ──────────────────────
export function renderItemsSection(
  full:              api.DealFull,
  active:            api.DealConfiguration | null,
  editConfig:        EditConfigState | null,
  itemMenuId:        string | null,
  expandedServerIds: ReadonlySet<string> = new Set(),
  serverMenuId:      string | null = null,
  /** 서버 구성품 소스 dropdown 후보. 아직 안 불러왔으면 빈 pool. */
  itemSources:     SourcePool = { loading: false, stock: [], rfq: [] },
  /** '견적 계산' 드롭다운이 열려 있는가 (판매/임대 선택). */
  quoteKindMenuOpen: boolean = false,
): string {
  // 확정(is_locked) 구성을 맨 앞으로, 그 안에서 config_no 순 (ITEM-003 §4.3.5)
  const configs = [...(full.configurations ?? [])].sort((a, b) => {
    if (a.is_locked !== b.is_locked) return a.is_locked ? -1 : 1;
    return a.config_no - b.config_no;
  });
  const locked  = active?.is_locked ?? false;
  // 미확정 구성 = 항상 편집 모드(구성 수정 버튼 폐기). 확정 구성 = 읽기전용.
  const editing = active != null && !locked && editConfig?.configId === active.id;
  // 저장 안 된 변경이 있으면 저장/취소 버튼 노출 + 노란 border.
  const dirty   = editing && editConfig != null && editConfigDirty(editConfig.items, editConfig.baseline);

  // ── 구성 탭 ── (확정된 구성은 '확정된 구성' 배지. 편집 중엔 탭/새구성 비활성)
  const tabs = configs.map((c) => {
    const isActive = active != null && c.id === active.id;
    const confirmed = c.is_locked;
    // 삭제 가능: 확정 안 됨 + 구성 2개 이상 + 저장 안 된 변경 없음. 탭 위 작은 x.
    const canDelete = !confirmed && configs.length > 1 && !dirty;
    // 저장 안 된 변경(dirty)이 있으면 탭 전환 차단(편집 유실 방지).
    return `<span class="deal-config-tab${isActive ? ' is-active' : ''}${confirmed ? ' is-confirmed' : ''}">
                <button class="deal-config-tab-label" type="button" ${dirty ? 'disabled' : ''}
                        data-action="config-select" data-config-id="${escapeHtml(c.id)}">
                  ${escapeHtml(c.name)}
                </button>
                ${confirmed ? '<span class="deal-config-confirmed-badge">확정된 구성</span>' : ''}
                ${canDelete
                  ? `<button class="deal-config-tab-del" type="button" title="구성 삭제" aria-label="구성 삭제"
                             data-action="config-delete" data-config-id="${escapeHtml(c.id)}">×</button>`
                  : ''}
              </span>`;
  }).join('');

  // 헤더 우측 액션 — 확정 토글만. 미확정=품목 확정 / 확정=품목 확정 취소.
  //   (구성 수정 버튼 폐기 — 미확정은 상시 편집이므로 별도 진입 불필요.)
  const headerAction = !active ? ''
    : (locked
        ? `<button class="btn btn-ghost btn-sm" type="button"
                     data-action="config-unconfirm-open" data-config-id="${escapeHtml(active.id)}">품목 확정 취소</button>`
        : `<button class="btn btn-primary btn-sm" type="button"
                     data-action="config-confirm-open" data-config-id="${escapeHtml(active.id)}">품목 확정</button>`);

  const header = `
      <header class="deal-section-header">
        <h2 class="deal-section-title">품목</h2>
        <div class="deal-config-tabs">
          ${tabs}
          <button class="btn btn-ghost btn-sm" type="button" data-action="config-create"
                  ${dirty ? 'disabled' : ''}>+새 구성</button>
        </div>
        ${headerAction ? `<div class="deal-items-header-action">${headerAction}</div>` : ''}
      </header>`;

  if (editing && editConfig) {
    // has-changes: dirty 일 때만 저장/취소 노출(CSS). 재렌더 없이 필드변경 시 토글도 이 클래스.
    const sectionCls = `deal-section deal-section-editing${dirty ? ' has-changes' : ''}`;
    const groups = collectServerGroups(activeItems(full, active));
    return `<section class="${sectionCls}">${header}${renderItemsEditing(
      editConfig, itemMenuId, full.id,
      groups, expandedServerIds, serverMenuId, itemSources,
    )}</section>`;
  }
  return `<section class="deal-section">${header}${renderItemsReadonly(
    full, active, locked, itemMenuId,
    collectServerGroups(activeItems(full, active)), expandedServerIds, serverMenuId,
    itemSources, quoteKindMenuOpen,
  )}</section>`;
}

// ── 읽기 모드 ──
function renderItemsReadonly(
  full:              api.DealFull,
  active:            api.DealConfiguration | null,
  locked:            boolean,
  itemMenuId:        string | null,
  serverGroups:      ServerGroup[],
  expandedServerIds: ReadonlySet<string>,
  serverMenuId:      string | null,
  itemSources:     SourcePool,
  /** '견적 계산' 드롭다운이 열려 있는가 (판매/임대 선택). */
  quoteKindMenuOpen: boolean,
): string {
  // 서버 트리(헤더행·자식행)는 별도 그룹 블록으로 그린다 — 플랫 목록에서 제외.
  const items = activeItems(full, active)
    .filter((it) => it.line_type !== 'server' && it.parent_id == null);
  const serverRows = serverGroups
    .map((g) => renderServerGroup(
      g, expandedServerIds.has(String(g.server.id)), serverMenuId, locked, itemMenuId,
      itemSources))
    .join('');

  // 구성 수정은 헤더(품목 확정 옆), 구성 삭제는 탭의 x 로 이동 — footer 엔 니즈 생성만.
  const partRows = items.length === 0 && serverRows === ''
    ? `<tr><td colspan="8" class="text-muted text-center">등록된 품목 없음</td></tr>`
    : items.map((it) => {
        return `
          <tr data-deal-item-id="${escapeHtml(String(it.id))}">
            <td class="deal-item-check"><input type="checkbox" data-action="item-check" aria-label="니즈 생성 선택" /></td>
            <td>
              ${regBadge(it.is_unregistered, it.is_pending)}${it.option_part ? `<code>${escapeHtml(it.option_part)}</code>` : ''}
            </td>
            <td>${escapeHtml(it.description_raw ?? it.item_description ?? '-')}</td>
            <td>${escapeHtml(it.requested_condition ?? '-')}</td>
            <td class="text-right">${it.quantity != null ? escapeHtml(String(it.quantity)) + ' EA' : '-'}</td>
            <td class="deal-item-src">${renderItemSourceSelect(
              itemSources, it.option_part, it.source_type, it.source_ref_id,
              String(it.id), locked,
            )}</td>
            <td class="text-right">
              <input type="text" inputmode="numeric" class="deal-sell-price-input"
                     data-action="item-sell-price" data-deal-item-id="${escapeHtml(String(it.id))}"
                     value="${escapeHtml(fmtThousands(it.sell_unit_price_krw))}" placeholder="-" aria-label="판매 단가" />
            </td>
            <td class="text-center deal-tag-cell">
              <button class="btn btn-ghost btn-icon deal-item-menu-trigger" type="button"
                      data-action="item-menu-open" data-deal-item-id="${escapeHtml(String(it.id))}"
                      title="품목 작업" aria-label="품목 작업">⋯</button>
              ${itemMenuId === String(it.id) ? `
                <div class="deal-tag-pop-overlay" data-action="item-menu-close"></div>
                <div class="deal-tag-pop deal-item-menu">
                  <button class="deal-tag-opt" type="button"
                          style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                          data-action="item-need-create" data-deal-item-id="${escapeHtml(String(it.id))}">니즈 생성</button>
                </div>` : ''}
            </td>
          </tr>
        `;
      }).join('');

  const tableConfirmedClass = locked ? ' is-confirmed' : '';
  // 견적 계산/바로 견적은 품목 확정 후에만 (ITEM-004 / §4.4.14-15)
  // 견적 계산은 **유형을 먼저 고르고** 들어간다 (마이그 178).
  //   판매와 임대는 입력 항목도 결과도 다르므로, 모달 안에서 갈아타게 두면
  //   "지금 뭘 만들고 있는지" 가 흐려진다. 진입 시점에 정하면 모달은 한 가지
  //   일만 한다. '바로 견적'은 판매 단가 기준 가견적이라 여기 끼지 않는다.
  const quoteActions = locked
    ? `<span class="deal-quote-kind-wrap">
           <button class="btn btn-primary btn-sm deal-quote-kind-trigger" type="button"
                   data-action="quote-kind-open" aria-haspopup="true"
                   aria-expanded="${quoteKindMenuOpen ? 'true' : 'false'}">견적 계산 ${quoteKindMenuOpen ? '▲' : '▼'}</button>
           ${quoteKindMenuOpen ? `
             <div class="deal-tag-pop-overlay" data-action="quote-kind-close"></div>
             <div class="deal-tag-pop deal-quote-kind-menu">
               <button class="deal-tag-opt" type="button"
                       style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                       data-action="create-quote" data-quote-type="SALE">판매 견적 계산</button>
               <button class="deal-tag-opt" type="button"
                       style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                       data-action="create-quote" data-quote-type="RENTAL">임대 견적 계산</button>
             </div>` : ''}
         </span>
         <button class="btn btn-ghost btn-sm" type="button" data-action="quick-quote" title="판매 단가 기준 즉시 견적(가견적)">바로 견적</button>
         <button class="btn btn-primary btn-sm" type="button" data-action="quote-onepage" title="가견적을 만들고 원페이지(지면 편집)로 바로">원페이지</button>`
    : '';

  return `
      <div class="deal-table-wrap">
        <table class="deal-items-table${tableConfirmedClass}">
          <thead>
            <tr>
              <th class="deal-item-check">
                <input type="checkbox" data-action="items-select-all" aria-label="전체 선택/해제" />
              </th>
              <th>파트넘버</th>
              <th>디스크립션</th>
              <th>컨디션</th>
              <th class="text-right">요청 수량</th>
              <th>소스</th>
              <th class="text-right">판매 단가</th>
              <th class="text-center">작업</th>
            </tr>
          </thead>
          <tbody>${serverRows}${partRows}</tbody>
        </table>
      </div>

      <footer class="deal-section-footer item-action-bar">
        <div class="item-action-left">
          <button class="btn btn-ghost btn-sm" type="button" data-action="need-create-open">니즈 생성</button>
        </div>
        <div class="item-action-right">${quoteActions}</div>
      </footer>
    `;
}

// ── 편집 모드 ──
function renderItemsEditing(
  edit:              EditConfigState,
  itemMenuId:        string | null,
  dealId:            string,
  serverGroups:      ServerGroup[],
  expandedServerIds: ReadonlySet<string>,
  serverMenuId:      string | null,
  itemSources:     SourcePool,
): string {
  const baseById = new Map(edit.baseline.map((b) => [b.lineId, b]));
  // 서버 그룹은 드래프트(평면 모델)에 없다 — deal_item 트리에서 직접 그린다.
  //   구성 변경은 별탭 편집기(수정)만 하므로 여기서는 읽기 + ⋯메뉴만 제공.
  const serverRows = serverGroups
    .map((g) => renderServerGroup(
      g, expandedServerIds.has(String(g.server.id)), serverMenuId, false, itemMenuId,
      itemSources))
    .join('');
  const partRows = edit.items
    .map((it) => renderEditRow(it, baseById.get(it.lineId), itemMenuId, itemSources))
    .join('');
  const rows = (serverRows === '' && partRows === '')
    ? `<tr><td colspan="8" class="text-muted text-center">품목을 추가해 주세요.</td></tr>`
    : serverRows + partRows;

  // 409 병합 배너 (동시성 §3-1) — 드래프트는 그대로, 확인 후 재저장 유도
  const conflictBanner = edit.conflict
    ? `<div class="deal-edit-conflict-banner" role="alert">⚠️ ${escapeHtml(edit.conflict)}</div>`
    : '';

  return `
      ${conflictBanner}
      <div class="deal-table-wrap">
        <table class="deal-items-table deal-items-edit">
          <thead>
            <tr>
              <th class="deal-item-check">
                <input type="checkbox" data-action="items-select-all" aria-label="전체 선택/해제" />
              </th>
              <th>파트넘버</th>
              <th>디스크립션</th>
              <th>
                <select class="input deal-edit-cond-bulk" data-action="edit-cond-bulk"
                        title="선택하면 전 품목 컨디션 일괄 적용" aria-label="컨디션 일괄 적용">
                  <option value="">컨디션 일괄적용</option>
                  ${CONDITION_OPTIONS.map((c) => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`).join('')}
                </select>
              </th>
              <th class="text-right">수량</th>
              <th>소스</th>
              <th class="text-right">판매 단가</th>
              <th class="text-center">작업</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr>
              <td colspan="8">
                <button class="btn btn-ghost btn-sm deal-edit-add" type="button"
                        data-action="edit-add-row">＋ 개별 품목 추가</button>
                <!-- 서버 구성 편집기(별탭) 진입 — 서버는 여기서만 만든다.
                     저장 원천은 이 딜의 deal_item 서브트리(server_config 없음). -->
                <button class="btn btn-ghost btn-sm deal-edit-add" type="button"
                        data-action="server-add"
                        data-deal-id="${escapeHtml(dealId)}"
                        data-config-id="${escapeHtml(edit.configId)}">＋ 서버 추가</button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <footer class="deal-section-footer item-action-bar">
        <div class="item-action-left">
          <button class="btn btn-ghost btn-sm" type="button" data-action="need-create-open">니즈 생성</button>
          <span class="deal-edit-unsaved-hint">저장되지 않은 변경이 있습니다 (Ctrl+S 가능)</span>
        </div>
        <div class="item-action-right deal-edit-actions">
          <button class="btn btn-ghost btn-sm" type="button" data-action="edit-cancel"
                  ${edit.saving ? 'disabled' : ''}>취소</button>
          <button class="btn btn-primary btn-sm" type="button" data-action="edit-complete"
                  ${edit.saving ? 'disabled' : ''}>${edit.saving ? '저장 중…' : '저장'}</button>
        </div>
      </footer>
    `;
}

function renderEditRow(
  it: EditDraftItem, base: EditDraftItem | undefined,
  itemMenuId: string | null, itemSources: SourcePool,
): string {
  const lid = escapeHtml(it.lineId);
  const cur = it.requested_condition ?? '';
  // baseline 대비 "기존 행의 변경"만 노란 border(.is-dirty). 신규 행(base 없음)은
  //   변경이 아니라 validation 대상이므로 border 안 줌(전체 has-changes 는 별개로 트리거).
  const dc = (f: 'option_part' | 'item_description' | 'requested_condition' | 'quantity') =>
    (base && editItemFieldDirty(it, base, f)) ? ' is-dirty' : '';
  // 검색으로 파트 교체 시 파트/디스크립션은 읽기전용 code/div 라 input dc 로는 안 잡힘.
  //   기존 행에서 파트넘버/등록상태가 바뀌면 읽기전용 표시에도 dirty 부여(신규 행 제외).
  const partChanged = !!base
    && (editItemFieldDirty(it, base, 'option_part') || !!it.is_unregistered !== !!base.is_unregistered);
  const roDirty = partChanged ? ' is-dirty' : '';
  // 판매단가 변경(기존 행) → input 노란 border.
  const _norm = (v: unknown): string => (v == null ? '' : String(v));
  const priceDirty = (base && _norm(it.sell_unit_price_krw) !== _norm(base.sell_unit_price_krw)) ? ' is-dirty' : '';
  // 신규 행(base 없음)이 내용을 가지면(복제 등) 행 전체 강조. 빈 신규행은 제외(validation 대상).
  const newRowCls = (!base && !!((it.option_part ?? '').trim() || (it.item_description ?? '').trim()))
    ? ' deal-edit-row-new' : '';
  // 비하드웨어(워런티·라이선스·서비스·배송비, 마이그 145)는 컨디션·소스가 없는 물건이다.
  //   신품/중고를 물을 대상이 아니고 재고·RFQ 로 매입하는 것도 아니다.
  //   그래서 두 칸을 비활성 '해당 없음' 으로 잠근다(2026-08-14 현민).
  //   백엔드의 품목 확정 검증에도 같은 예외가 있다 — 컨디션 없이 확정된다.
  const nonHw = !!it.is_non_hw;
  // 컨디션 옵션 (미지정 없음). 기존 값이 표준 6개에 없으면 레거시 옵션으로 보존.
  const condOpts = [...CONDITION_OPTIONS] as string[];
  if (cur && !condOpts.includes(cur)) condOpts.unshift(cur);
  const condOptions = nonHw
    ? '<option value="" selected>해당 없음</option>'
    : `<option value="">— 선택 —</option>` + condOpts.map((c) =>
        `<option value="${escapeHtml(c)}"${c === cur ? ' selected' : ''}>${escapeHtml(c)}</option>`).join('');
  // 미등록 여부는 명시 플래그로 판단 (§4.5) — 파트넘버 유무와 독립.
  const unreg = it.is_unregistered;
  // 파트넘버 칸: 배지 + 파트넘버(항상 읽기전용 텍스트) + 🔍 검색 아이콘.
  //   파트넘버는 품목 검색/미등록 지정으로만 확정 — 미등록 품목도 p/n 직접 수정 불가(§4.5).
  const partInner = `${regBadge(unreg, it.is_pending)}<code class="deal-edit-part-code${roDirty}">${escapeHtml(it.option_part ?? '')}</code>`;
  // 디스크립션: 등록·임시등록(카탈로그 원문) 행은 읽기전용. 미등록·신규 행만 자유 편집.
  const descFixed = !unreg && !!(it.option_part ?? '').trim();
  const roTitle = it.is_pending ? '임시등록 품목 — 임시등록 페이지에서 수정' : '카탈로그 원문 — 수정 불가';
  const descCell = descFixed
    ? `<div class="deal-edit-desc-ro${roDirty}" title="${escapeHtml(roTitle)}">${escapeHtml(it.description_raw ?? it.item_description ?? '-')}</div>`
    : `<input class="deal-edit-input${dc('item_description')}" type="text" placeholder="디스크립션 입력"
                 data-action="edit-field" data-line-id="${lid}" data-field="item_description"
                 value="${escapeHtml(it.item_description ?? '')}" />`;
  // 409 병합으로 편입된 외부(봇) 추가 행 — 하이라이트 + 배지
  const extClass = it.externalAdded ? ' deal-edit-row-external' : '';
  const extBadge = it.externalAdded ? `<span class="cond-chip deal-edit-external-badge">봇 추가</span> ` : '';
  // 니즈 생성 선택 체크박스 — 저장된 행(it.id 존재)만. 미저장 신규행은 제외.
  //   실제 deal_item id 를 체크박스에 실어 getCheckedItemIds 가 니즈 생성에 사용.
  const checkCell = it.id == null
    ? ''
    : `<input type="checkbox" data-action="item-check" data-item-id="${escapeHtml(String(it.id))}" aria-label="니즈 생성 선택" />`;
  return `
      <tr data-line-id="${lid}" class="deal-edit-row${extClass}${newRowCls}">
        <td class="deal-item-check">${checkCell}</td>
        <td>
          <div class="deal-edit-part-cell">
            ${extBadge}${partInner}
            <button class="deal-edit-search-icon" type="button" title="품목 검색"
                    data-action="edit-search-open" data-line-id="${lid}">🔍</button>
          </div>
        </td>
        <td>${descCell}</td>
        <td>
          <select class="deal-edit-input${dc('requested_condition')}" data-action="edit-category"
                  data-line-id="${lid}" data-field="requested_condition"
                  ${nonHw ? 'disabled title="비하드웨어 품목은 컨디션이 없습니다."' : ''}>${condOptions}</select>
        </td>
        <td class="text-right">
          <input class="deal-edit-input deal-edit-num${dc('quantity')}" type="number" min="1" step="1" placeholder="수량"
                 data-action="edit-field" data-line-id="${lid}" data-field="quantity"
                 value="${escapeHtml(it.quantity)}" />
        </td>
        <td class="deal-item-src">${nonHw
          ? `<select class="deal-item-srcsel" disabled
                     title="비하드웨어 품목은 매입 소스를 지정하지 않습니다."><option>해당 없음</option></select>`
          : renderItemSourceSelect(
              itemSources, it.option_part, it.source_type, it.source_ref_id,
              it.id ?? null, false,
            )}</td>
        <td class="text-right">
          <input class="deal-edit-input deal-edit-num${priceDirty}" type="text" inputmode="numeric" placeholder="판매 단가"
                 data-action="edit-field" data-line-id="${lid}" data-field="price"
                 value="${escapeHtml(fmtThousands(it.sell_unit_price_krw))}" aria-label="판매 단가" />
        </td>
        <td class="text-center deal-tag-cell">
          <button class="btn btn-ghost btn-icon deal-item-menu-trigger" type="button"
                  data-action="item-menu-open" data-deal-item-id="${lid}" title="작업" aria-label="작업">⋯</button>
          ${itemMenuId === it.lineId ? `
            <div class="deal-tag-pop-overlay" data-action="item-menu-close"></div>
            <div class="deal-tag-pop deal-item-menu">
              <button class="deal-tag-opt" type="button"
                      style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                      data-action="edit-duplicate-row" data-line-id="${lid}">복제</button>
              <button class="deal-tag-opt text-danger" type="button"
                      style="width:100%;border:0;background:transparent;text-align:left;font:inherit"
                      data-action="edit-delete-row" data-line-id="${lid}">삭제</button>
            </div>` : ''}
        </td>
      </tr>
    `;
}

// 검색 모달 적용 버튼 — 상태별 morph(적용 버튼 하나로 통합).
//   · 검색 전 / 결과 있는데 미선택 → "적용"(비활성)
//   · 결과 선택 → "적용"(활성) → 등록 카탈로그 품목 적용
//   · 검색 진행 + 결과 없음 → "미등록 품목으로 적용"(활성) → 미등록 품목으로 변경
export function renderItemSearchApplyBtn(s: ItemSearchState): string {
  const searched = !s.loading && s.query.trim().length >= 2;
  const qtyOf = (h: api.CatalogSearchHit): number => Number((h as Record<string, unknown>).qty_available ?? 0) || 0;
  const shown = s.stockOnly ? s.hits.filter((h) => qtyOf(h) > 0) : s.hits;
  const noResults = searched && shown.length === 0;
  if (noResults) {
    return `<button class="btn btn-primary" type="button" data-action="item-search-apply-unreg"
                    id="item-search-apply">미등록 품목으로 적용</button>`;
  }
  return `<button class="btn btn-primary" type="button" data-action="item-search-apply"
                  id="item-search-apply" ${s.selectedKey ? '' : 'disabled'}>적용</button>`;
}

// ── 카탈로그 검색 패널 (편집 모드 파트넘버 교체) ──
export function renderItemSearchPanel(s: ItemSearchState, fxRate: number): string {
  return `
      <div class="deal-rfq-modal${s.open ? '' : ' hidden'}" data-item-search-modal>
        <div class="deal-rfq-modal-overlay" data-action="item-search-overlay"></div>
        <div class="deal-rfq-modal-dialog deal-item-search-dialog" role="dialog" aria-labelledby="item-search-title">
          <header class="deal-rfq-modal-header">
            <h2 id="item-search-title">품목 검색</h2>
            <button class="deal-rfq-modal-close" type="button" aria-label="닫기"
                    data-action="item-search-close">✕</button>
          </header>
          <div class="deal-rfq-modal-body">
            <p class="deal-item-search-sub text-muted text-sm" style="margin:0 0 8px">파트넘버 또는 디스크립션 기준으로 검색합니다.</p>
            <div class="deal-item-search-controls" style="display:flex;align-items:center;gap:10px">
              <div class="deal-item-search-inputwrap" style="flex:1;position:relative">
                <button class="deal-item-search-clear" type="button" data-action="item-search-clear"
                        title="검색어 지우기" aria-label="검색어 지우기">✕</button>
                <input class="deal-item-search-input" type="text" style="width:100%;padding-left:34px"
                       data-action="item-search-input" value="${escapeHtml(s.query)}"
                       placeholder="파트넘버 또는 디스크립션 검색 (2글자 이상)" autocomplete="off" />
              </div>
              <label style="display:flex;align-items:center;gap:6px;white-space:nowrap;font-size:13px">
                <input type="checkbox" data-action="item-search-stock-toggle" ${s.stockOnly ? 'checked' : ''} /> 재고 있는 품목만 검색
              </label>
            </div>
            <div class="deal-item-search-results" id="item-search-results">
              ${renderItemSearchResultsHtml(s, fxRate)}
            </div>
          </div>
          <footer class="deal-rfq-modal-footer">
            <span id="item-search-apply-slot">${renderItemSearchApplyBtn(s)}</span>
            <button class="btn btn-ghost" type="button" data-action="item-search-close">취소</button>
          </footer>
        </div>
      </div>
    `;
}

export function renderItemSearchResultsHtml(s: ItemSearchState, fxRate: number): string {
  if (s.loading) return '<div class="deal-item-search-empty">검색 중…</div>';
  if (s.query.trim().length < 2) return '<div class="deal-item-search-empty">2글자 이상 입력해 주세요.</div>';
  const qtyOf = (h: api.CatalogSearchHit) => Number((h as Record<string, unknown>).qty_available ?? 0) || 0;
  // '재고 있는 품목만' — qty_available > 0 만 표시 (§4.5.3-4)
  const shown = s.stockOnly ? s.hits.filter((h) => qtyOf(h) > 0) : s.hits;
  if (shown.length === 0) return '<div class="deal-item-search-empty">일치하는 품목이 없습니다.</div>';
  return shown.map((hit) => {
    const key = `${hit.source}::${hit.option_part}`;
    // 전 사이트 일관: 제조사 원문(description_raw) 우선, 없으면 정제 description 폴백
    const desc = catalogHitDescription(hit);
    const isSel = key === s.selectedKey;
    const qty = qtyOf(hit);
    const cond = hit.stock_condition ? String(hit.stock_condition) : '';
    const stockLabel = qty > 0 ? `재고: ${cond ? `${escapeHtml(cond)} ` : ''}${qty}` : '재고 없음';
    const costUsd = typeof hit.stock_cost_usd === 'number' ? hit.stock_cost_usd : null;
    // 재고원가는 값이 없어도 항상 표기 ('-')
    const costLabel = costUsd != null
      ? `재고 원가 ₩${Math.round(costUsd * fxRate).toLocaleString('ko-KR')}`
      : '재고 원가 -';
    return `
        <button class="deal-item-search-row${isSel ? ' is-selected' : ''}" type="button"
                data-action="item-search-select" data-key="${escapeHtml(key)}">
          <code class="deal-item-search-part">${escapeHtml(hit.option_part)}</code>
          <span class="deal-item-search-desc" title="${escapeHtml(desc)}">${escapeHtml(desc)}</span>
          <span class="deal-item-search-stock">${stockLabel}</span>
          <span class="deal-item-search-cost">${costLabel}</span>
        </button>
      `;
  }).join('');
}
